import { coreReleaseCandidates, supportedCoreVersions } from '@emma/core/core';
import { Button, ButtonGroup, Dropdown, Modal, Text, useToast } from '@lego/klik-ui';
import type { InferRequestType, InferResponseType } from 'hono/client';
import { useState } from 'react';
import { type ApiClient, useMutation } from '@/hooks/api';
import { ToastOptions } from '@/utility/ToastOptions';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  deviceIds?: number[];
  fleetId?: number;
  workspaceId: number;
  deviceCount: number;
  onSuccess: () => void;
};

const CORE_VERSION_OPTIONS = [...supportedCoreVersions, ...coreReleaseCandidates];

type PostBatchUpdateCoreVersion =
  ApiClient['v1']['workspaces'][':workspaceId']['devices']['batch-update-core-version']['$post'];
type BatchUpdateCoreVersionBody = InferRequestType<PostBatchUpdateCoreVersion>['json'];
type BatchUpdateCoreVersionResponse = InferResponseType<PostBatchUpdateCoreVersion, 200>;

export const BatchUpdateCoreVersionModal = ({
  isOpen,
  onClose,
  deviceIds,
  fleetId,
  workspaceId,
  deviceCount,
  onSuccess,
}: Props) => {
  const toast = useToast();
  const [selectedVersion, setSelectedVersion] = useState<string>(CORE_VERSION_OPTIONS[0]);
  const [isLoading, setIsLoading] = useState(false);

  const batchUpdateCoreVersion = useMutation<
    BatchUpdateCoreVersionResponse,
    { workspaceId: number } & BatchUpdateCoreVersionBody
  >('batch-update-core-version', (client, { workspaceId, ...json }) =>
    client.v1.workspaces[':workspaceId'].devices['batch-update-core-version'].$post({
      param: { workspaceId: workspaceId.toString() },
      json,
    })
  );

  const buildRequestBody = (desiredCoreVersion: string) => {
    const base = { workspaceId, desiredCoreVersion };
    if (deviceIds && deviceIds.length > 0) return { ...base, deviceIds };
    if (fleetId !== undefined) return { ...base, fleetId };
    return null;
  };

  const handleConfirm = async () => {
    const body = buildRequestBody(selectedVersion);
    if (!body) return;

    setIsLoading(true);
    try {
      const result = await batchUpdateCoreVersion(body);
      const succeededCount = result.succeeded.length;
      const failedCount = result.failed.length;

      if (failedCount === 0) {
        toast(
          ToastOptions.success(
            'Core version updated',
            `Successfully updated ${succeededCount} device${succeededCount !== 1 ? 's' : ''} to ${selectedVersion}`,
            5000
          )
        );
      } else {
        toast({
          ...ToastOptions.error(
            'Partial update',
            `${succeededCount} updated, ${failedCount} failed: ${result.failed.map((f) => `Device #${f.deviceId}: ${f.error}`).join('; ')}`
          ),
          duration: 8000,
        });
      }

      onSuccess();
      onClose();
    } catch {
      // Already toasted and logged by the mutation hook; caught only to
      // avoid an unhandled promise rejection here.
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setSelectedVersion(CORE_VERSION_OPTIONS[0]);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl">
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>Update Core Version</Modal.Header>
        <Modal.CloseButton />
        <Modal.Body>
          <Text mb={4}>
            {deviceIds?.length
              ? `This will update the desired core version for ${deviceCount} selected device${deviceCount !== 1 ? 's' : ''}.`
              : 'This will update the desired core version for all devices in this fleet.'}
          </Text>
          <Dropdown
            onChange={(val: string) => setSelectedVersion(val)}
            size="sm"
            value={[selectedVersion]}
            width={150}
          >
            {CORE_VERSION_OPTIONS.map((version) => (
              <Dropdown.Option key={version} value={version}>
                {version}
              </Dropdown.Option>
            ))}
          </Dropdown>
        </Modal.Body>
        <Modal.Footer>
          <ButtonGroup>
            <Button isLoading={isLoading} onClick={handleConfirm}>
              Confirm
            </Button>
            <Button isDisabled={isLoading} onClick={handleClose} variant="outline">
              Cancel
            </Button>
          </ButtonGroup>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};
